import React, {FC, useState, useEffect} from "react";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { Button, ModalVariables, StartPage } from "../../../../../../components";
import { billingService } from "../../../../../../api";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../../../../utils/display-price";
import { icons } from "../../../../../../icons";
import { useNavigate, useParams } from "react-router-dom";
import { useUi } from "../../../../../../context/ui.context";
import { compareArrays } from "../../../../../../utils/findIntersection";

const Invoices:FC = () => {
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  const { userId } = useParams();
  const createInvoiceLabel = t('CREATE_INVOICE', { defaultValue: 'Create invoice' });
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [paginationData, setPaginationData] = useState<any>();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalType, setModalType] = useState<any>('');
  const [alertTitle, setAlertTitle] = useState<any>('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>();
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [checked, setChecked] = useState<any[]>([]);

  const editChecked = (item:any, isAll?:boolean) => {
    if(item === null) {
      setChecked([]);
      setAllChecked(false);
      return;
    }
    const founded = checked.find((selected:any) => selected.id === item?.id);
    if(founded) {
      if(isAll){
        return;
      }
      setChecked((prev:any) => prev.filter((selected:any) => selected.id !== item?.id));
      setAllChecked(false);
    } else {
      setChecked((prev) => [...prev, item]);
    }
  };

  useEffect(() => {
    if(compareArrays(invoices, checked) && checked?.length !== 0){
      setAllChecked(true);
    }
  },[checked, invoices]);

  const openModal = (modal:any, items:any[], title:string) => {
    setModalType(modal);
    setAlertTitle(title);
    setAlertSubTitle(items);
    setViewModal(true);
    setActiveAnimation(true);
  };

  const getInvoices = () => {
    billingService.getInvoiceList({
      page:page || 1,
      page_size:pageSize || 10,
      ordering:ordering || '-issue_date',
      search:searchString||'',
      client:userId
    })
    .then((res) => {
      setInvoices(res.data.objects.map((invoice:any) => {
        return {
          ...invoice,
          name: invoice.display_number || invoice.name || invoice.id,
          issue_date: timeConverterMMDDYYHHMM(invoice.issue_date),
          navigate:`/billing/invoices/${invoice.id.toString()}`,
          total:formatCurrency(invoice.total, invoice.currency),
          
        }
      }))
      setPaginationData(res.data)
      setPageLoading(false)
      setAllChecked(false);
      setChecked([]);
    })
    .catch((e) => {
      console.log('error', e);
      setPageLoading(false)
    })
  }
  useEffect(() => {
    getInvoices()
    const interval = setInterval(() => {
      getInvoices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString])
  const ColumnsInvoices = [
    {
      title:'ID',
      key:'id'
    },
    {
      title:t("DISPLAY_NUMBER"),
      key:'display_number'
    },
    {
      title:t("OPERATION_STATUS"),
      key:'status',
      sorted:true,
    },
    {
      title:t("ISSUE_DATE"),
      sorted:true,
      key:'issue_date'
    },
    {
      title:t("TOTAL"),
      key:'total'
    },
    {
      title:'',
      key:'props'
    }
  ]

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
    setSelectedItem(null);
    setModalType('');
    setAlertSubTitle(undefined);
  };

  const closeModal = async () => {
    setLoading(true);
    try {
      if (allChecked) {
        await Promise.all(invoices.map((invoice:any) => billingService.deleteInvoice(invoice.id)));
        setAllChecked(false);
        setChecked([]);
        await getInvoices();
      } else if (checked?.length > 0) {
        await Promise.all(checked.map((invoice:any) => billingService.deleteInvoice(invoice.id)));
        setChecked([]);
        await getInvoices();
      } else if (selectedItem) {
        await billingService.deleteInvoice(selectedItem.id);
        await getInvoices();
      }
    } catch (e:any) {
      if (e?.response?.data?.detail) {
        viewAlert({
          severity: 'error',
          message: e.response.data.detail,
        });
      }
    } finally {
      setSelectedItem(null);
      setViewModal(false);
      setActiveAnimation(false);
      setLoading(false);
      setModalType('');
      setAlertSubTitle(undefined);
    }
  };

  const contextMenu = [
    {
      title:t("EDIT"),
      id:'edit',
      icon:icons.Edit,
      onClick: (item:any) => {
        navigate(`/billing/invoices/${item.id}/edit`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'delete',
      onClick:(item:any) => {
        setSelectedItem(item);
        openModal('delete', [item], t("INVOICE"));
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  return (
    <>
    {/* @ts-ignore */}
      <StartPage
        title={`${t("INVOICES")}`}
        noResourceText={`${t("NO_INVOICES")}`}
        createResourceButton={createInvoiceLabel}
        createResourceButtonEventOrLink={() => navigate('/billing/invoices/create')}
        haveResource={invoices.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightButtonComponentCustomWithResources={
          <div style={{display:'flex', alignItems:'center'}}>
            <Button
              onClick={() => {
                const itemsToDelete = allChecked ? invoices : checked;
                if(!itemsToDelete || itemsToDelete.length === 0) return;
                setSelectedItem(null);
                openModal('delete', itemsToDelete, itemsToDelete.length > 1 ? t("INVOICES") : t("INVOICE"));
              }}
              variant='icon'
              size='display'
              test_id='button-delete'
              disabled={!allChecked && checked?.length === 0 }
              customStyles={{margin:'0px 6px 0px 0px'}}
            >
              <img src={icons.DeleteIcon}/>
            </Button>
          </div>
        }
        tableData={{
          columns:ColumnsInvoices,
          data:invoices,
          props:contextMenu,
          paginationData:paginationData,
          updateData:getInvoices,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        instance={selectedItem}
      />
    </>
  )
}

export default Invoices
